import fp from "lodash/fp";
import { List } from "immutable";
import { Dispatch } from "react-redux";
import { IStore } from "reducers";
import { COLOR_SWATCHES } from "constants/action-types";
import { IBaseParams, ISortOrder } from "constants/types";
import { ISwatch, SwatchType } from "constants/color-swatches";

export const fetch = (params?: IBaseParams, next?: boolean) => (dispatch: Dispatch<{}>) => {
    if (!next) {
        return dispatch({ type: COLOR_SWATCHES.LIST.FETCH.START, payload: { url: "/api/v3.0/color-swatches" } });
    }
};

export const setHex = (color: string, value: string) => (dispatch: Dispatch<{}>, getState: () => IStore) => {
    const updated = fp.uniqBy("color", [
        { color, value, swatch_type: SwatchType.HEX, modified_on: new Date().valueOf() },
        ...getState().colorSwatches.list.payload,
    ]);
    dispatch({ type: COLOR_SWATCHES.LIST.UPDATE, payload: updated });
    const body = new FormData();
    body.set("color", color);
    body.set("value", value);
    dispatch({
        type: COLOR_SWATCHES.LIST.SET.START,
        payload: { url: "/api/v3.0/color-swatches/hex", body, type: SwatchType.HEX },
    });
};

export const setFile = (color: string, file: File) => (dispatch: Dispatch<{}>) => {
    const body = new FormData();
    body.append("file", file);
    body.set("color", color);
    dispatch({
        type: COLOR_SWATCHES.LIST.SET.START,
        payload: { url: "/api/v3.0/color-swatches/file", body, type: SwatchType.FILE },
    });
};

export const uploadHex = (file: File) => (dispatch: Dispatch<{}>) => {
    const body = new FormData();
    body.append("file", file);
    dispatch(resetImport());
    dispatch({ type: COLOR_SWATCHES.IMPORT.UPLOAD.START, payload: { url: "/api/v3.0/color-swatches/hex/bulk", body } });
};

export const uploadFiles = (files: File[]) => (dispatch: Dispatch<{}>) => {
    const body = new FormData();
    fp.forEach((file: File) => body.append("file", file), files);
    dispatch(resetImport());
    dispatch({
        type: COLOR_SWATCHES.IMPORT.UPLOAD.START,
        payload: { url: "/api/v3.0/color-swatches/file/bulk", body },
    });
};

export const exportSwatches =
    (params: IBaseParams, list?: string[]) => (dispatch: Dispatch<{}>, getState: () => IStore) => {
        const colors = list || fp.map("color")(applyFilters(getState().colorSwatches.list.payload, params));
        dispatch(resetExport());
        dispatch({
            type: COLOR_SWATCHES.EXPORT.DOWNLOAD.START,
            payload: { url: "/api/v3.0/color-swatches/export/csv", body: { colors } },
        });
    };

export const deleteSwatches = (colors: string[]) => (dispatch: Dispatch<{}>, getState: () => IStore) => {
    if (!fp.isEmpty(colors)) {
        const update = fp.uniqBy("color", [
            ...fp.map((color) => ({ color }))(colors),
            ...getState().colorSwatches.list.payload,
        ]);
        dispatch({ type: COLOR_SWATCHES.LIST.UPDATE, payload: update });
        dispatch({ type: COLOR_SWATCHES.LIST.DELETE, payload: { url: "/api/v3.0/color-swatches", body: { colors } } });
    }
};

export const deleteAllSwatches = () => (dispatch: Dispatch<{}>, getState: () => IStore) => {
    dispatch({
        type: COLOR_SWATCHES.LIST.UPDATE,
        payload: fp.map(fp.pick("color"))(getState().colorSwatches.list.payload),
    });
    dispatch({
        type: COLOR_SWATCHES.LIST.DELETE,
        payload: { url: "/api/v3.0/color-swatches", body: { colors: null } },
    });
};

export const resetSetting = () => (dispatch: Dispatch<{}>, getState: () => IStore) =>
    dispatch({
        type: COLOR_SWATCHES.LIST.SET.SUCCESS,
        payload: getState().colorSwatches.list.payload,
    });

export const resetImport = () => ({
    type: COLOR_SWATCHES.IMPORT.UPDATE,
    payload: { payload: null, isUploading: false },
});

export const resetExport = () => ({
    type: COLOR_SWATCHES.EXPORT.UPDATE,
    payload: { payload: null, isExporting: false },
});

export function applyFilters(list: ISwatch[], params: IBaseParams): ISwatch[] {
    return fp.compose(
        sort(params?.sort, params?.sortOrder),
        filter(params.filters),
        search(fp.trim(params.search))
    )(list);
}

const search = (term: string) =>
    term ? fp.filter(({ color }) => new RegExp(fp.escapeRegExp(term), "i").test(color)) : fp.identity;
const sort = (field: string, order: ISortOrder) =>
    fp.compose(order === "desc" ? reverse(field) : fp.identity, fp.sortBy(field));

// put items without `field` value to the end in the desc sorted list
const reverse = (field: string) =>
    fp.compose(
        ([a, b]) => [...fp.reverse(a), ...b],
        fp.partition((x) => !!x[field])
    );

const filter = (filters: IBaseParams["filters"]) =>
    filters?.size
        ? fp.filter((swatch: ISwatch) =>
              filters.every((value, key) => {
                  switch (key) {
                      case "__type":
                          return List()
                              .concat(value)
                              .some((type) =>
                                  type === SwatchType.NONE ? !swatch.swatch_type : swatch.swatch_type === type
                              );
                      case "__modified_on_lte":
                          return new Date(Number(value)) >= new Date(swatch.modified_on);
                      case "__modified_on_gte":
                          return new Date(Number(value)) <= new Date(swatch.modified_on);
                      default:
                          return true;
                  }
              })
          )
        : fp.identity;
